/**
 * The CJK font for PDF export, fetched on demand.
 *
 * jsPDF's built-in faces are Helvetica/Times/Courier, all WinAnsi — so a
 * Chinese supplier name was either mojibake or had to be drawn as an image.
 * Images worked but were soft, unselectable and inflated the file, which is
 * the wrong trade for a document people file and forward.
 *
 * WHY ON DEMAND, NOT BUNDLED
 * The subset is 2.5MB. Shipping that in the main bundle would punish every
 * user — including the English-only ones — on an app whose premise is cold-
 * booting on a bad connection. Instead it is a static asset fetched the first
 * time a report actually contains Chinese, and the service worker caches it
 * (`isBuildAsset` matches .ttf), so it costs one download and then works
 * offline like everything else.
 *
 * WHY THIS PARTICULAR FILE
 * jsPDF can embed TrueType `glyf` outlines but NOT CFF, which rules out most
 * CJK fonts — they ship as OTF precisely because CJK glyf tables are huge.
 * @fontsource's "chinese-simplified" slice of Noto Sans SC is glyf-flavoured
 * and already subset to the common set, which is what makes 2.5MB possible
 * instead of ~16MB. See public/fonts/NotoSansSC-LICENSE.txt.
 */

const URL_TTF = '/fonts/NotoSansSC-Regular-subset.ttf';
export const CJK_FONT = 'NotoSansSC';
const VFS_NAME = 'NotoSansSC-Regular-subset.ttf';

/** Resolves to the base64 font, or null if it could not be fetched. */
let loading = null;
let base64 = null;
/** Once a fetch has failed we stop retrying for this session. */
let failed = false;

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  // Chunked, because String.fromCharCode(...bytes) blows the argument limit
  // on a 2.5MB array.
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return window.btoa(binary);
}


/**
 * Fetch and cache the font bytes. Never throws — a failure just means the
 * caller falls back to rasterising, which still produces a correct document.
 */
export async function loadCjkFont() {
  if (base64) return base64;
  if (failed) return null;
  if (!loading) {
    loading = (async () => {
      try {
        const res = await fetch(URL_TTF);
        if (!res.ok) throw new Error(`font ${res.status}`);
        base64 = toBase64(await res.arrayBuffer());
        return base64;
      } catch (e) {
        failed = true;
        loading = null;
        return null;
      }
    })();
  }
  return loading;
}

/**
 * Register the font on a jsPDF document. Returns true when the document can
 * subsequently `setFont(CJK_FONT)`.
 */
export function registerCjkFont(doc) {
  if (!base64) return false;
  try {
    doc.addFileToVFS(VFS_NAME, base64);
    doc.addFont(VFS_NAME, CJK_FONT, 'normal');
    // jsPDF needs a bold entry to exist or setFont(font, 'bold') throws; the
    // subset has no bold face, so bold maps to the same outlines. Synthetic
    // weight is not worth another 2.5MB.
    doc.addFont(VFS_NAME, CJK_FONT, 'bold');
    return true;
  } catch (e) {
    return false;
  }
}
